import { Component } from '@angular/core';

@Component({
  selector: 'app-user-manual',
  templateUrl: './user-manual.component.html',
  styleUrls: ['./user-manual.component.scss'],
})
export class UserManualComponent {
  // Theme rotation for cards (mirrors join-lomy-cards themes)
  themes: string[] = ['purple', 'pink', 'blue', 'dark-blue'];
  // Upute za potvrdu kupona. Namijenjeno vlasnicima i djelatnicima, ne kupcima:
  // opisuje sto osoblje radi na blagajni, a ne kako kupac koristi aplikaciju.
  sections = [
    {
      title: 'Prijava djelatnika u aplikaciju',
      purpose:
        'Kupone potvrđujete u LOMY mobilnoj aplikaciji, istim računom kojim se prijavljujete i u ovu konzolu. Nije potreban poseban uređaj ni dodatni račun.',
      lists: [
        {
          title: 'Koraci:',
          items: [
            'Preuzmite LOMY aplikaciju na mobitel s kojim radite za blagajnom',
            'Prijavite se svojom e-mail adresom i lozinkom (vlasnik ili djelatnik)',
            'Aplikacija vas automatski otvara u načinu za osoblje - vidjet ćete skener umjesto vlastitih bodova',
          ],
        },
        {
          title: 'Nemate račun za djelatnika?',
          items: [
            'Vlasnik ga dodaje u konzoli, u izborniku Djelatnici',
            'Djelatnik dobiva pozivnicu e-mailom i sam postavlja lozinku',
          ],
        },
      ],
      images: [],
    },
    {
      title: 'Potvrda kupona na blagajni',
      purpose:
        'Kupac vam pokaže kupon na svom mobitelu. Vi ga skenirate i potvrdite - tek tada je kupon iskorišten i nestaje s njegovog popisa.',
      lists: [
        {
          title: 'Koraci:',
          items: [
            'Dodirnite LOMY znak na sredini donje trake u aplikaciji - otvara se skener',
            'Zamolite kupca da otvori kupon koji želi iskoristiti',
            'Usmjerite kameru na QR kod prikazan na kupčevom mobitelu',
            'Provjerite naziv kupona koji vam aplikacija pokaže i potvrdite',
          ],
        },
        {
          title: 'Na što pripaziti:',
          items: [
            'Kupon se može potvrditi samo jednom - nakon potvrde postaje "Iskorišten"',
            'Ako aplikacija javi da je kupon istekao ili već iskorišten, kupac ga više ne može upotrijebiti',
            'Kupac ne može sam označiti kupon kao iskorišten na blagajni; potvrda uvijek ide preko vas',
          ],
        },
      ],
      images: [],
    },
    {
      title: 'Kupon koji je kupac već aktivirao',
      purpose:
        'Kupac može kupon aktivirati sam, prije nego dođe do vas. Tada mu na ekranu teče odbrojavanje od dvije minute.',
      lists: [
        {
          title: 'Što napraviti:',
          items: [
            'Pogledajte kupčev ekran dok odbrojavanje još traje i provjerite naziv kupona',
            'Ako odbrojavanje istekne, kupon se automatski označava kao iskorišten',
            'U tom slučaju ne treba ništa skenirati',
          ],
        },
      ],
      images: [],
    },
    {
      title: 'Kuponi s vanjskim kodom (Wolt i slično)',
      purpose:
        'Neki kuponi ne vrijede na blagajni nego kod dostave. Kupac dobiva jedinstveni kod koji upisuje u aplikaciji za dostavu, pa nema QR koda za skeniranje.',
      lists: [
        {
          title: 'Kako ih prepoznati:',
          items: [
            'Kupac nema QR kod, nego kod koji kopira (npr. SOI-7TKQ-8YR6)',
            'Kod se provjerava kod kanala dostave, ne kod nas - mi ne vidimo je li iskorišten',
          ],
        },
        {
          title: 'Ako ga treba zatvoriti ručno:',
          items: [
            'U konzoli otvorite Kupci i pronađite kupca',
            'U popisu njegovih kupona odaberite "Označi kao iskorišten"',
            'Ovo je nepovratno, pa napravite tek kad ste sigurni da je kod iskorišten',
          ],
        },
      ],
      images: [],
    },
  ];

  // Carousel state: current slide index per section
  currentSlides: number[] = this.sections.map(() => 0);
  // touch swipe support
  private touchStartX: number | null = null;
  private readonly swipeThreshold = 40; // px

  nextSlide(sectionIndex: number) {
    const imgs = this.sections[sectionIndex].images || [];
    if (!imgs.length) return;
    this.currentSlides[sectionIndex] = (this.currentSlides[sectionIndex] + 1) % imgs.length;
  }

  prevSlide(sectionIndex: number) {
    const imgs = this.sections[sectionIndex].images || [];
    if (!imgs.length) return;
    this.currentSlides[sectionIndex] = (this.currentSlides[sectionIndex] - 1 + imgs.length) % imgs.length;
  }

  goToSlide(sectionIndex: number, slideIndex: number) {
    const imgs = this.sections[sectionIndex].images || [];
    if (!imgs.length) return;
    this.currentSlides[sectionIndex] = Math.max(0, Math.min(slideIndex, imgs.length - 1));
  }

  // Touch handlers per-card
  onTouchStart(evt: TouchEvent) {
    this.touchStartX = evt.changedTouches?.[0]?.clientX ?? null;
  }

  onTouchEnd(evt: TouchEvent, sectionIndex: number) {
    if (this.touchStartX == null) return;
    const endX = evt.changedTouches?.[0]?.clientX ?? this.touchStartX;
    const delta = endX - this.touchStartX;
    if (Math.abs(delta) > this.swipeThreshold) {
      if (delta < 0) this.nextSlide(sectionIndex);
      else this.prevSlide(sectionIndex);
    }
    this.touchStartX = null;
  }

  // =============================
  // Lightbox (fullscreen carousel)
  // =============================
  isLightboxOpen = false;
  lightboxSectionIndex: number | null = null;
  lightboxSlideIndex = 0;

  openLightbox(sectionIndex: number, slideIndex: number = 0) {
    this.lightboxSectionIndex = sectionIndex;
    this.lightboxSlideIndex = slideIndex;
    this.isLightboxOpen = true;
    // prevent body scroll while lightbox is open
    try { document?.body?.classList.add('no-scroll'); } catch {}
  }

  closeLightbox() {
    this.isLightboxOpen = false;
    this.lightboxSectionIndex = null;
    this.lightboxSlideIndex = 0;
    try { document?.body?.classList.remove('no-scroll'); } catch {}
  }

  nextLightbox() {
    if (this.lightboxSectionIndex == null) return;
    const imgs = this.sections[this.lightboxSectionIndex].images || [];
    if (!imgs.length) return;
    this.lightboxSlideIndex = (this.lightboxSlideIndex + 1) % imgs.length;
  }

  prevLightbox() {
    if (this.lightboxSectionIndex == null) return;
    const imgs = this.sections[this.lightboxSectionIndex].images || [];
    if (!imgs.length) return;
    this.lightboxSlideIndex = (this.lightboxSlideIndex - 1 + imgs.length) % imgs.length;
  }

  goToLightboxSlide(i: number) {
    if (this.lightboxSectionIndex == null) return;
    const imgs = this.sections[this.lightboxSectionIndex].images || [];
    if (!imgs.length) return;
    this.lightboxSlideIndex = Math.max(0, Math.min(i, imgs.length - 1));
  }

  // Lightbox swipe support
  private lbTouchStartX: number | null = null;
  onLbTouchStart(evt: TouchEvent) {
    this.lbTouchStartX = evt.changedTouches?.[0]?.clientX ?? null;
  }
  onLbTouchEnd(evt: TouchEvent) {
    if (this.lbTouchStartX == null) return;
    const endX = evt.changedTouches?.[0]?.clientX ?? this.lbTouchStartX;
    const delta = endX - this.lbTouchStartX;
    if (Math.abs(delta) > this.swipeThreshold) {
      if (delta < 0) this.nextLightbox();
      else this.prevLightbox();
    }
    this.lbTouchStartX = null;
  }

  // Keyboard support in lightbox
  onLightboxKeydown(evt: KeyboardEvent) {
    if (!this.isLightboxOpen) return;
    if (evt.key === 'Escape') this.closeLightbox();
    else if (evt.key === 'ArrowRight') this.nextLightbox();
    else if (evt.key === 'ArrowLeft') this.prevLightbox();
  }
}
